/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない。
 */
import { useTranslation } from 'react-i18next'

import { Badge, HStack, Text } from '@enechain/ecloud-designsystem'

import {
  AuditCell,
  MasterDataTable,
  type MasterDataTableColumn,
  standardRowActions,
} from '~/components/common/MasterDataTable'
import type { PlantMasterRow } from '~/hooks/useListPlantMasters'
import { convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import { decimalToNumber } from '~/models/decimal'
import { derivedPowerTypeLabel } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/labels'
import { ExpiredBadge } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredBadge'
import { formatCivilDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'
import { voltageClassLabel } from '~/pages/AdminOcctoMaster/tabs/_common/models/voltageClass'

type Props = {
  rows: PlantMasterRow[]
  isLoading: boolean
  onEdit: (row: PlantMasterRow) => void
  onDelete: (row: PlantMasterRow) => void
  today: CivilDate
  /** 空 state のメッセージ上書き (AC-09)。 */
  emptyLabel?: string
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

export const PlantMasterTable = ({
  rows,
  isLoading,
  onEdit,
  onDelete,
  today,
  emptyLabel,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // エリア列: このエリアと発電所のエリアが違うユニットがあれば、同じセルの中に印を添える (中身は hover)
  const areaCell = (row: PlantMasterRow): React.ReactNode => {
    const items = row.areaMismatchedUnits
    if (items.length === 0) {
      return convertAreaLabel(row.areaCode)
    }
    return (
      <HStack gap={1}>
        <Text as="span" whiteSpace="nowrap">
          {convertAreaLabel(row.areaCode)}
        </Text>
        <Badge
          whiteSpace="nowrap"
          status="warning"
          title={items
            .map((item) =>
              t('page:adminOcctoMaster.plantMaster.areaMismatchUnit', {
                unit: item.unitName,
                site: item.siteName,
                siteArea: convertAreaLabel(item.siteAreaCode),
              }),
            )
            .join('\n')}
        >
          {t('page:adminPlantUnitMaster.unit.missing.areaMismatch')}
        </Badge>
      </HStack>
    )
  }

  const columns: Array<MasterDataTableColumn<PlantMasterRow>> = [
    // [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // design (`design/admin/occto.html` 発電所タブ) はステータス列を持たない
    // (適用開始日 / 適用終了日 の列も無く、 ステータス列の対象タブに入っていない) が、
    // 行の状態は他の属性を読む前に分かる必要があるため一覧の左端へ統一した。
    {
      key: 'status',
      header: t('page:adminOcctoMaster.common.column.status'),
      cell: (row) => (
        <ExpiredBadge
          today={today}
          validFrom={row.validFrom}
          validTo={row.validTo}
        />
      ),
    },
    {
      key: 'plantCode',
      header: t('page:adminOcctoMaster.plantMaster.column.plantCode'),
      cell: (row) => row.plantCode,
    },
    {
      key: 'plantName',
      header: t('page:adminOcctoMaster.plantMaster.column.plantName'),
      cell: (row) => row.plantName,
    },
    {
      key: 'plantNameAbbr',
      header: t('page:adminOcctoMaster.plantMaster.column.plantNameAbbr'),
      cell: (row) => row.plantNameAbbr,
    },
    {
      key: 'areaCode',
      header: t('page:adminOcctoMaster.plantMaster.column.areaCode'),
      cell: areaCell,
    },
    {
      key: 'powerType',
      header: t('page:adminOcctoMaster.plantMaster.column.powerType'),
      cell: (row) => derivedPowerTypeLabel(row.powerType),
    },
    {
      key: 'voltageClass',
      header: t('page:adminOcctoMaster.plantMaster.column.voltageClass'),
      cell: (row) => voltageClassLabel(row.voltageClass),
    },
    {
      key: 'maxReceivingPowerKw',
      header: t('page:adminOcctoMaster.plantMaster.column.maxReceivingPowerKw'),
      format: 'number',
      cell: (row) => {
        const val = decimalToNumber(row.maxReceivingPowerKw)
        return val !== undefined ? val.toLocaleString() : '—'
      },
    },
    {
      key: 'businessName',
      header: t('page:adminOcctoMaster.plantMaster.column.businessName'),
      cell: (row) => row.businessName,
    },
    {
      key: 'validFrom',
      header: t('page:adminOcctoMaster.common.column.validFrom'),
      cell: (row) => formatCivilDate(row.validFrom),
    },
    {
      key: 'validTo',
      header: t('page:adminOcctoMaster.common.column.validTo'),
      cell: (row) =>
        row.validTo !== undefined ? formatCivilDate(row.validTo) : '—',
    },
    {
      key: 'updatedAt',
      header: t('page:adminOcctoMaster.plantMaster.column.updatedAt'),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onEdit}
      renderRowActions={standardRowActions(onEdit, onDelete)}
      rows={rows}
    />
  )
}
